import React, { useEffect, useState } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Search,
    RefreshCw,
    Eye,
    Check,
    X,
    Calendar,
    User,
    Package,
    Tag,
    FileText,
    Image as ImageIcon,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { getAdminInvestmentTransactionsList, approvePaymentRequest_Admin, rejectPaymentRequest_Admin } from "@/api/Admin/payment";
import { PaymentAdminAPI } from "@/api/Admin/type";
import ImagePreview from "@/components/ui/ImagePreview";
import dayjs from 'dayjs';

const StatusBadge = ({ status }: { status: string }) => {
    const statusLower = status?.toLowerCase() || '';
    const stylesMap: Record<string, string> = {
        pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
        approved: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400',
        completed: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400',
        rejected: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
        cancelled: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-400',
    };

    const labelsMap: Record<string, string> = {
        pending: 'Đang chờ',
        approved: 'Đã duyệt',
        completed: 'Thành công',
        rejected: 'Bị từ chối',
        cancelled: 'Đã hủy',
    };

    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${stylesMap[statusLower] || 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-400'}`}>
            {labelsMap[statusLower] || status}
        </span>
    );
};

const formatCurrency = (amount: any, currency: string = 'đ') => {
    const val = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('vi-VN').format(val || 0) + ' ' + currency;
};

export default function InvestmentTransactionAdmin() {
    const [data, setData] = useState<PaymentAdminAPI | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchCode, setSearchCode] = useState('');

    const [detailModal, setDetailModal] = useState<{ open: boolean, data: any | null }>({ open: false, data: null });
    const [actionModal, setActionModal] = useState<{ open: boolean, type: 'approve' | 'reject' | null, id: number | null, data: any | null }>({ open: false, type: null, id: null, data: null });
    const [adminNote, setAdminNote] = useState('');
    const [receivedAmount, setReceivedAmount] = useState<number>(0);

    const fetchData = () => {
        setLoading(true);
        getAdminInvestmentTransactionsList({
            page,
            per_page: pageSize,
            code: searchCode
        }).then(res => {
            setData(res);
        }).catch(() => {
            alert('Không thể tải danh sách giao dịch đầu tư');
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchData();
    }, [page, pageSize]);

    const handleSearch = () => {
        setPage(1);
        fetchData();
    };

    const handleOpenDetail = (item: any) => {
        setDetailModal({ open: true, data: item });
    };

    const handleOpenAction = (type: 'approve' | 'reject', item: any) => {
        setActionModal({ open: true, type, id: item.id, data: item });
        setAdminNote('');
        setReceivedAmount(item.package_info?.paid_so_far || 0);
    };

    const handleConfirmAction = async () => {
        if (!actionModal.id || !actionModal.type) return;

        setLoading(true);
        try {
            if (actionModal.type === 'approve') {
                await approvePaymentRequest_Admin(actionModal.id, adminNote, receivedAmount);
            } else {
                await rejectPaymentRequest_Admin(actionModal.id, adminNote);
            }
            alert(`${actionModal.type === 'approve' ? 'Duyệt' : 'Từ chối'} yêu cầu thành công`);
            fetchData();
            setActionModal({ open: false, type: null, id: null, data: null });
        } catch (error) {
            // Error managed by API helper
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <Helmet>
                <title>Quản lý Giao dịch Đầu tư - Admin</title>
            </Helmet>

            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Giao dịch Đầu tư</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Quản lý và phê duyệt các yêu cầu nạp tiền đầu tư từ người dùng</p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1 flex gap-2">
                        <div className="relative flex-1 max-w-xs">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="w-4 h-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Tìm theo mã giao dịch..."
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                                value={searchCode}
                                onChange={e => setSearchCode(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            />
                        </div>
                        <button
                            onClick={handleSearch}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            Tìm kiếm
                        </button>
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="flex items-center gap-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Làm mới
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Người dùng</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Gói</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Số tiền nạp</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mã giao dịch</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ngày tạo</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trạng thái</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                                        Đang tải dữ liệu...
                                    </td>
                                </tr>
                            ) : data?.data?.data && data.data.data.length > 0 ? (
                                data.data.data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold mr-3 overflow-hidden">
                                                    {item.user_info?.avatar_url ? (
                                                        <img src={item.user_info?.avatar_url} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        item.user_info?.fullname?.charAt(0) || 'U'
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{item.user_info?.fullname}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{item.user_info?.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">{item.package_info?.package_name || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-bold text-gray-900 dark:text-white">
                                                {formatCurrency(item.package_info?.paid_so_far)}
                                            </div>
                                            {item.package_info?.remaining && item.package_info.remaining > 0 && (
                                                <div className="text-[10px] text-orange-500 dark:text-orange-400">
                                                    Còn lại: {formatCurrency(item.package_info.remaining)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 font-mono">
                                            {item.payment_code}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                            {item.created_at}
                                        </td>
                                        <td className="px-6 py-4">
                                            <StatusBadge status={item.status} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                                    onClick={() => handleOpenDetail(item)}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                {item.status === 'pending' && (
                                                    <>
                                                        <button
                                                            className="p-1.5 text-emerald-600 hover:text-emerald-700 dark:text-emerald-500 dark:hover:text-emerald-400 rounded bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 transition"
                                                            onClick={() => handleOpenAction('approve', item)}
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 rounded bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-800 transition"
                                                            onClick={() => handleOpenAction('reject', item)}
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                                        Không có yêu cầu giao dịch nào
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        Hiển thị {data?.data?.from || 0} đến {data?.data?.to || 0} trong tổng số {data?.data?.total || 0}
                    </div>
                    <div className="flex gap-2">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-4 h-4 dark:text-white" />
                        </button>
                        <button
                            disabled={page * pageSize >= (data?.data?.total || 0)}
                            onClick={() => setPage(p => p + 1)}
                            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight className="w-4 h-4 dark:text-white" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Action Modal */}
            {actionModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {actionModal.type === 'approve' ? 'Phê duyệt yêu cầu' : 'Từ chối yêu cầu'}
                            </h3>
                        </div>
                        
                        <div className="p-6 space-y-6">
                             {actionModal.data && (
                                <div className="space-y-4">
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                                            <label className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 block mb-3">Thông tin người dùng</label>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <User className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{actionModal.data.user_info?.fullname}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-gray-400" />
                                                    <span className="text-xs text-gray-600 dark:text-gray-400">{actionModal.data.user_info?.email}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag className="w-4 h-4 text-gray-400" />
                                                    <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">{actionModal.data.payment_code}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                                            <label className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 block mb-3">Thông tin gói đầu tư</label>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Package className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{actionModal.data.package_info?.package_name}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-xs text-gray-600 dark:text-gray-400">{dayjs(actionModal.data.created_at).format('DD/MM/YYYY HH:mm')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        <div className="border border-gray-100 dark:border-gray-700 p-4 rounded-lg text-center bg-white dark:bg-gray-800">
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Giá trị gói</div>
                                            <div className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(actionModal.data.package_info?.total_price)}</div>
                                        </div>
                                        <div className="border border-blue-100 dark:border-blue-900/30 p-3 rounded-lg text-center bg-blue-50/50 dark:bg-blue-900/10">
                                            <div className="text-[10px] text-blue-600 dark:text-blue-400 mb-1">Số tiền đã nạp</div>
                                            <div className="text-base font-bold text-blue-700 dark:text-blue-300">{formatCurrency(actionModal.data.package_info?.paid_so_far)}</div>
                                        </div>
                                        <div className="border border-gray-100 dark:border-gray-700 p-3 rounded-lg text-center bg-white dark:bg-gray-800">
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">Số dư còn lại</div>
                                            <div className="text-base font-bold text-gray-900 dark:text-white">{formatCurrency(actionModal.data.package_info?.remaining)}</div>
                                        </div>
                                    </div>

                                    {actionModal.data.image_url && (
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 block mb-2 flex items-center gap-2">
                                                <ImageIcon className="w-3 h-3" /> Bằng chứng thanh toán
                                            </label>
                                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden inline-block">
                                                <ImagePreview
                                                    src={actionModal.data.image_url}
                                                    alt="Bằng chứng thanh toán"
                                                    className="w-32 h-32 object-cover"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {actionModal.data.user_note && (
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
                                            <label className="text-[10px] uppercase font-bold text-blue-500 dark:text-blue-400 block mb-1">Ghi chú từ người dùng</label>
                                            <p className="text-sm text-blue-800 dark:text-blue-300 italic">"{actionModal.data.user_note}"</p>
                                        </div>
                                    )}

                                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                                        {actionModal.type === 'approve' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Số tiền thực nhận</label>
                                                <input
                                                    type="number"
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                                                    value={receivedAmount}
                                                    onChange={e => setReceivedAmount(Number(e.target.value) || 0)}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ghi chú của Admin</label>
                                            <textarea
                                                rows={3}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                                                placeholder="Nhập ghi chú..."
                                                value={adminNote}
                                                onChange={e => setAdminNote(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                             )}
                        </div>

                        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900/20">
                            <button
                                onClick={() => setActionModal({ open: false, type: null, id: null, data: null })}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleConfirmAction}
                                disabled={loading}
                                className={`px-4 py-2 text-white rounded-lg transition shadow-sm ${
                                    actionModal.type === 'approve' 
                                    ? 'bg-emerald-600 hover:bg-emerald-700' 
                                    : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                {loading ? 'Đang xử lý...' : 'Xác nhận'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {detailModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Chi tiết giao dịch đầu tư</h3>
                            <button onClick={() => setDetailModal({ open: false, data: null })} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="p-6">
                            {detailModal.data && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                                            <label className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 block mb-3">Thông tin người dùng</label>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <User className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{detailModal.data.user_info?.fullname}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600 dark:text-gray-400">{detailModal.data.user_info?.email}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Tag className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600 dark:text-gray-400 font-mono text-xs">{detailModal.data.payment_code}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                                            <label className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 block mb-3">Thông tin gói đầu tư</label>
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Package className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{detailModal.data.package_info?.package_name}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600 dark:text-gray-400">{dayjs(detailModal.data.created_at).format('DD/MM/YYYY HH:mm')}</span>
                                                </div>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <StatusBadge status={detailModal.data.status} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="border border-gray-100 dark:border-gray-700 p-4 rounded-lg text-center bg-white dark:bg-gray-800">
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Giá trị gói</div>
                                            <div className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(detailModal.data.package_info?.total_price)}</div>
                                        </div>
                                        <div className="border border-blue-100 dark:border-blue-900/30 p-4 rounded-lg text-center bg-blue-50/50 dark:bg-blue-900/10">
                                            <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">Số tiền đã nạp</div>
                                            <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{formatCurrency(detailModal.data.package_info?.paid_so_far)}</div>
                                        </div>
                                        <div className="border border-gray-100 dark:border-gray-700 p-4 rounded-lg text-center bg-white dark:bg-gray-800">
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Số dư còn lại</div>
                                            <div className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(detailModal.data.package_info?.remaining)}</div>
                                        </div>
                                    </div>

                                    {detailModal.data.image_url && (
                                        <div className="mt-4">
                                            <label className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 block mb-2 flex items-center gap-2">
                                                <ImageIcon className="w-3 h-3" /> Bằng chứng thanh toán
                                            </label>
                                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden inline-block">
                                                <ImagePreview
                                                    src={detailModal.data.image_url}
                                                    alt="Bằng chứng thanh toán"
                                                    className="w-40 h-40 object-cover"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {detailModal.data.user_note && (
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                                            <label className="text-[10px] uppercase font-bold text-blue-500 dark:text-blue-400 block mb-1">Ghi chú từ người dùng</label>
                                            <p className="text-sm text-blue-800 dark:text-blue-300 italic">"{detailModal.data.user_note}"</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end bg-gray-50 dark:bg-gray-900/20">
                            <button
                                onClick={() => setDetailModal({ open: false, data: null })}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}